// ts-rest contract for Comply > My Page.
//
// Composes the compliance officer's personalised workspace headline tiles and
// chart series server-side (the page was previously all hard-coded "—" tiles
// and "Awaiting source" empty states). Reads route through the same ontology
// surfaces the Comply dashboard already consumes (Breach, KycReview, TcRecord,
// ConsumerDutyPillar) plus the firm SMCR directory, so the page stays a thin
// renderer.
//
// Gated to `comply` (and `view` oversight) personas; non-comply personas get
// a 403.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const ragStatusSchema = z.enum(['Green', 'Amber', 'Red']);

// Headline tiles.
export const complyMyPageTilesSchema = z.object({
    openBreaches: z.number().int(),
    kycReviewsDue: z.number().int(),
    // Whole-number percentage (0–100) of in-date T&C competency records.
    tcCompletionPct: z.number().int(),
    smcrPending: z.number().int(),
});
export type ComplyMyPageTiles = z.infer<typeof complyMyPageTilesSchema>;

// Breach status trend — open / resolved counts per recent month.
export const complyBreachTrendPointSchema = z.object({
    month: z.string(),
    open: z.number().int(),
    resolved: z.number().int(),
});
export type ComplyBreachTrendPoint = z.infer<typeof complyBreachTrendPointSchema>;

// KYC expiry pipeline — reviews bucketed by how soon they fall due.
export const complyKycExpiryBucketSchema = z.object({
    bucket: z.string(),
    count: z.number().int(),
});
export type ComplyKycExpiryBucket = z.infer<typeof complyKycExpiryBucketSchema>;

// Consumer Duty RAG — one row per FCA pillar.
export const complyConsumerDutyRagSchema = z.object({
    pillar: z.string(),
    score: z.number().int(),
    rag: ragStatusSchema,
});
export type ComplyConsumerDutyRag = z.infer<typeof complyConsumerDutyRagSchema>;

// SMCR certification status — completed / due / overdue counts.
export const complySmcrStatusSchema = z.object({
    certified: z.number().int(),
    due: z.number().int(),
    overdue: z.number().int(),
});
export type ComplySmcrStatus = z.infer<typeof complySmcrStatusSchema>;

export const complyMyPageResponseSchema = z.object({
    tiles: complyMyPageTilesSchema,
    breachStatusTrend: z.array(complyBreachTrendPointSchema),
    kycExpiryPipeline: z.array(complyKycExpiryBucketSchema),
    consumerDutyRag: z.array(complyConsumerDutyRagSchema),
    smcrStatus: complySmcrStatusSchema,
});
export type ComplyMyPageResponse = z.infer<typeof complyMyPageResponseSchema>;

const errorBody = z.object({ error: z.string() });

export const complyMyPageContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/my-page',
            responses: {
                200: complyMyPageResponseSchema,
                403: errorBody,
            },
            summary: 'Composed Comply > My Page payload (headline tiles + chart series)',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
